import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Alert, Select } from '@piggytechng/component-library';
import { IoCloseOutline } from 'react-icons/io5';
import { PiUploadBold, PiFileXls, PiDownloadSimpleBold, PiPlusBold, PiTrashBold } from 'react-icons/pi';
import { OverideSelectStyle } from '../../helpers/utils';
import useBulkFlag from '../../hooks/useBulkFlag';
import { USER_FLAG_OPTIONS } from '../../constants';
import { BulkFlagEditableRow, BulkFlagRow, BulkUnflagEditableRow, ModalBulkFlagUsersProps } from '../../types';
import './ModalBulkFlagUsers.scss';

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const normalizeKey = (key: string) => key.toLowerCase().replace(/[\s_-]/g, '');
const normalizeFlag = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

// Unrecognized flags resolve to '' so the admin must pick one before submitting.
const resolveFlagId = (raw: string): string => {
  const val = normalizeFlag(String(raw ?? ''));
  const match = USER_FLAG_OPTIONS.find((o) => normalizeFlag(o.value) === val || normalizeFlag(o.label) === val);
  return match?.value ?? '';
};

const isIncomplete = (row: BulkFlagEditableRow) => !row.userId.trim() || !row.flagId || !row.reason.trim();

const parseSheet = (file: File): Promise<Record<string, any>[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        if (!data) return reject(new Error('Empty file'));
        // Keep cells as text; IDs like `770598923e0125` otherwise parse as numbers.
        const workbook = XLSX.read(data, { type: 'array', raw: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '', raw: false }));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

const ModalBulkFlagUsers = ({ modalIsOpen, setIsOpen, initialTab = 'flag' }: ModalBulkFlagUsersProps) => {
  const [activeTab, setActiveTab] = useState<'flag' | 'unflag'>(initialTab);
  const [flagRows, setFlagRows] = useState<BulkFlagEditableRow[]>([]);
  const [showFlagErrors, setShowFlagErrors] = useState(false);
  const [unflagRows, setUnflagRows] = useState<BulkUnflagEditableRow[]>([]);
  const flagFileRef = useRef<HTMLInputElement>(null);
  const unflagFileRef = useRef<HTMLInputElement>(null);

  const { flagUsersInBulk, unblacklistUsers, isFlagging, isUnflagging } = useBulkFlag();

  const selectStyle = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  useEffect(() => {
    if (modalIsOpen) setActiveTab(initialTab);
  }, [modalIsOpen, initialTab]);

  const close = () => setIsOpen(false);

  const handleFlagFile = async (file: File) => {
    try {
      const rows = await parseSheet(file);
      const parsed: BulkFlagEditableRow[] = rows
        .map((row) => {
          const keyMap: Record<string, any> = {};
          Object.keys(row).forEach((k) => (keyMap[normalizeKey(k)] = row[k]));
          return {
            id: uid(),
            userId: String(keyMap['userid'] ?? '').trim(),
            flagId: resolveFlagId(keyMap['flagid'] || keyMap['flag']),
            reason: String(keyMap['reason'] || keyMap['flagnote'] || '').trim(),
          };
        })
        .filter((r) => r.userId || r.flagId || r.reason);

      const seen = new Set<string>();
      const mapped = parsed.filter((r) => {
        if (seen.has(r.userId)) return false;
        if (r.userId) seen.add(r.userId);
        return true;
      });

      if (!mapped.length) {
        Alert.error('No rows found in the file');
        return;
      }
      if (mapped.length < parsed.length) {
        Alert.warn(`Removed ${parsed.length - mapped.length} duplicate row(s)`);
      }
      setShowFlagErrors(false);
      setFlagRows(mapped);
    } catch {
      Alert.error('Could not read the file');
    }
  };

  const updateFlagRow = (id: string, patch: Partial<BulkFlagEditableRow>) =>
    setFlagRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeFlagRow = (id: string) => setFlagRows((rows) => rows.filter((r) => r.id !== id));
  const addFlagRow = () => setFlagRows((rows) => [...rows, { id: uid(), userId: '', flagId: '', reason: '' }]);
  const clearFlagRows = () => {
    setShowFlagErrors(false);
    setFlagRows([]);
  };

  const handleFlagAll = () => {
    const incompleteCount = flagRows.filter(isIncomplete).length;
    if (incompleteCount) {
      setShowFlagErrors(true);
      Alert.error(`Add a User ID, flag type and reason to the ${incompleteCount} highlighted row(s)`);
      return;
    }

    const payload: BulkFlagRow[] = flagRows.map((r) => ({
      userId: r.userId.trim(),
      flagId: r.flagId,
      reason: r.reason.trim(),
    }));
    flagUsersInBulk(payload, clearFlagRows);
  };

  const downloadFlagTemplate = () => {
    const sample = [{ userId: '10293847', flagId: 'freeze', reason: 'Multiple chargeback disputes' }];
    const ws = XLSX.utils.json_to_sheet(sample, { header: ['userId', 'flagId', 'reason'] });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'flag-users');
    XLSX.writeFile(wb, 'flag-users-template.xlsx');
  };

  const handleUnflagFile = async (file: File) => {
    try {
      const rows = await parseSheet(file);
      const mapped: BulkUnflagEditableRow[] = rows
        .map((row) => {
          const keyMap: Record<string, any> = {};
          Object.keys(row).forEach((k) => (keyMap[normalizeKey(k)] = row[k]));
          return { id: uid(), userId: String(keyMap['userid'] ?? '').trim() };
        })
        .filter((r) => r.userId);

      if (!mapped.length) {
        Alert.error('No user IDs found in the file');
        return;
      }
      setUnflagRows(mapped);
    } catch {
      Alert.error('Could not read the file');
    }
  };

  const updateUnflagRow = (id: string, userId: string) =>
    setUnflagRows((rows) => rows.map((r) => (r.id === id ? { ...r, userId } : r)));
  const removeUnflagRow = (id: string) => setUnflagRows((rows) => rows.filter((r) => r.id !== id));
  const addUnflagRow = () => setUnflagRows((rows) => [...rows, { id: uid(), userId: '' }]);

  const handleUnflagAll = () => {
    const users = Array.from(new Set(unflagRows.map((r) => r.userId.trim()).filter(Boolean)));
    if (!users.length) {
      Alert.error('Add at least one User ID');
      return;
    }
    unblacklistUsers(users, () => setUnflagRows([]));
  };

  const downloadUnflagTemplate = () => {
    const sample = [{ userId: '10293847' }, { userId: '10293850' }];
    const ws = XLSX.utils.json_to_sheet(sample, { header: ['userId'] });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'unflag-users');
    XLSX.writeFile(wb, 'unflag-users-template.xlsx');
  };

  if (!modalIsOpen) return null;

  return (
    <div className="bulk-flag-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="bulk-flag-modal">
        <div className="bulk-flag-modal__header">
          <h2>Bulk Flag / Unflag Users</h2>
          <button className="bulk-flag-modal__close" onClick={close}>
            <IoCloseOutline size={22} />
          </button>
        </div>

        <div className="bulk-flag-modal__tabs">
          <button
            className={activeTab === 'flag' ? 'active' : ''}
            onClick={() => setActiveTab('flag')}
            disabled={isFlagging || isUnflagging}
          >
            Flag Users
          </button>
          <button
            className={activeTab === 'unflag' ? 'active' : ''}
            onClick={() => setActiveTab('unflag')}
            disabled={isFlagging || isUnflagging}
          >
            Unflag Users
          </button>
        </div>

        {/* ---------------- FLAG ---------------- */}
        {activeTab === 'flag' && (
          <div className="bulk-flag-modal__body">
            {flagRows.length === 0 ? (
              <>
                <button className="bulk-flag-modal__template" onClick={downloadFlagTemplate}>
                  <PiDownloadSimpleBold size={16} /> Download template (userId, flagId, reason)
                </button>
                <div
                  className="bulk-flag-modal__dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (f) handleFlagFile(f);
                  }}
                  onClick={() => flagFileRef.current?.click()}
                >
                  <PiUploadBold size={28} />
                  <p className="bulk-flag-modal__dropzone-label">Upload spreadsheet</p>
                  <p className="bulk-flag-modal__dropzone-hint">CSV or Excel — columns: userId, flagId, reason</p>
                  <input
                    ref={flagFileRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={(e) => e.target.files?.[0] && handleFlagFile(e.target.files[0])}
                  />
                </div>
                <button className="bulk-flag-modal__add-manual" onClick={addFlagRow}>
                  <PiPlusBold size={14} /> Add a row manually
                </button>
              </>
            ) : (
              <>
                <div className="bulk-flag-modal__table-wrap">
                  <table className="bulk-flag-modal__table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>User ID</th>
                        <th>Flag Type</th>
                        <th>Reason</th>
                        <th style={{ width: 44 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {flagRows.map((row, i) => {
                        const flagError = showFlagErrors && !row.flagId;
                        return (
                          <tr
                            key={row.id}
                            className={showFlagErrors && isIncomplete(row) ? 'bulk-flag-modal__row--invalid' : ''}
                          >
                            <td>{i + 1}</td>
                            <td>
                              <input
                                className={showFlagErrors && !row.userId.trim() ? 'is-invalid' : ''}
                                value={row.userId}
                                placeholder="User ID"
                                onChange={(e) => updateFlagRow(row.id, { userId: e.target.value })}
                              />
                            </td>
                            <td className="bulk-flag-modal__select-cell">
                              <Select
                                {...selectStyle}
                                backgroundColor="#ffffff"
                                outlineColor={flagError ? '#ef4444' : '#e2e8f0'}
                                height="42px"
                                placeholder="Select flag"
                                options={USER_FLAG_OPTIONS}
                                value={row.flagId}
                                onChange={(val: string) => updateFlagRow(row.id, { flagId: val })}
                              />
                            </td>
                            <td>
                              <input
                                className={showFlagErrors && !row.reason.trim() ? 'is-invalid' : ''}
                                value={row.reason}
                                placeholder="Reason"
                                onChange={(e) => updateFlagRow(row.id, { reason: e.target.value })}
                              />
                            </td>
                            <td>
                              <button className="bulk-flag-modal__row-remove" onClick={() => removeFlagRow(row.id)}>
                                <PiTrashBold size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="bulk-flag-modal__footer">
                  <div className="bulk-flag-modal__footer-left">
                    <button className="bulk-flag-modal__ghost" onClick={addFlagRow}>
                      <PiPlusBold size={14} /> Add row
                    </button>
                    <button className="bulk-flag-modal__ghost" onClick={clearFlagRows}>
                      Clear all
                    </button>
                    <button className="bulk-flag-modal__ghost" onClick={() => flagFileRef.current?.click()}>
                      <PiFileXls size={16} /> Re-upload
                    </button>
                    <input
                      ref={flagFileRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={(e) => e.target.files?.[0] && handleFlagFile(e.target.files[0])}
                    />
                  </div>
                  <button className="bulk-flag-modal__primary" onClick={handleFlagAll} disabled={isFlagging}>
                    {isFlagging ? 'Flagging...' : `Flag All (${flagRows.length})`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------------- UNFLAG ---------------- */}
        {activeTab === 'unflag' && (
          <div className="bulk-flag-modal__body">
            {unflagRows.length === 0 ? (
              <>
                <button className="bulk-flag-modal__template" onClick={downloadUnflagTemplate}>
                  <PiDownloadSimpleBold size={16} /> Download template (userId)
                </button>
                <div
                  className="bulk-flag-modal__dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (f) handleUnflagFile(f);
                  }}
                  onClick={() => unflagFileRef.current?.click()}
                >
                  <PiUploadBold size={28} />
                  <p className="bulk-flag-modal__dropzone-label">Upload spreadsheet</p>
                  <p className="bulk-flag-modal__dropzone-hint">CSV or Excel — column: userId</p>
                  <input
                    ref={unflagFileRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={(e) => e.target.files?.[0] && handleUnflagFile(e.target.files[0])}
                  />
                </div>
                <button className="bulk-flag-modal__add-manual" onClick={addUnflagRow}>
                  <PiPlusBold size={14} /> Add a User ID manually
                </button>
              </>
            ) : (
              <>
                <div className="bulk-flag-modal__table-wrap">
                  <table className="bulk-flag-modal__table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>User ID</th>
                        <th style={{ width: 44 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {unflagRows.map((row, i) => (
                        <tr key={row.id}>
                          <td>{i + 1}</td>
                          <td>
                            <input
                              value={row.userId}
                              placeholder="User ID"
                              onChange={(e) => updateUnflagRow(row.id, e.target.value)}
                            />
                          </td>
                          <td>
                            <button className="bulk-flag-modal__row-remove" onClick={() => removeUnflagRow(row.id)}>
                              <PiTrashBold size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bulk-flag-modal__footer">
                  <div className="bulk-flag-modal__footer-left">
                    <button className="bulk-flag-modal__ghost" onClick={addUnflagRow}>
                      <PiPlusBold size={14} /> Add row
                    </button>
                    <button className="bulk-flag-modal__ghost" onClick={() => setUnflagRows([])}>
                      Clear all
                    </button>
                    <button className="bulk-flag-modal__ghost" onClick={() => unflagFileRef.current?.click()}>
                      <PiFileXls size={16} /> Re-upload
                    </button>
                    <input
                      ref={unflagFileRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={(e) => e.target.files?.[0] && handleUnflagFile(e.target.files[0])}
                    />
                  </div>
                  <button className="bulk-flag-modal__primary" onClick={handleUnflagAll} disabled={isUnflagging}>
                    {isUnflagging ? 'Unflagging...' : `Unflag All (${unflagRows.length})`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalBulkFlagUsers;
